import * as THREE from 'three'
import { Helper } from '../../../../../classes/helper'
import { useSidewall } from '../../../../../hooks/state/useSidewall'
import { CachedMeshPhysicalMaterial } from '../../../../material/cachedMeshPhysicalMaterial'
import { useLoader } from '@react-three/fiber'
import { useEffect } from 'react'
import { AR_REPLACEMENT_TARGET_MATERIAL_NAME } from '../../../../../hooks/extension.hook'
import { TextureLoader } from 'three'
import { useRoof } from '../../../../../hooks/state/useRoof'
import { useMeasures } from '../../../../../hooks/state/useMeasures'
import { Keys } from '../../../../../hooks/state/utlility/utils'

export const PlankWall = (): JSX.Element => {
  const {
    sidwallTyp,
    spaceBetween,
    height,
    sideWall,
    plankHeight,
    type,
    plankHeightTyp,
    plankHeigtInput,
    logBarSize,
    spaceY,
  } = useSidewall()
  const { color } = useRoof()
  const { userAngle, isQubus, attachedSize, userHeight } = useMeasures()

  const [colorMap, normalMap, roughnessMap] = useLoader(TextureLoader, [
    'texture/plank/planke.jpg',
    'texture/plank/planke_normal.jpg',
    'texture/plank/planke_rough.jpg',
  ])
  const heightComplete = plankHeight >= spaceY
  const sidewallLeftRight =
    type === Keys.Seitenwände.Values.SeitenwandLinks.key ||
    type === Keys.Seitenwände.Values.SeitenwandRechts.key

  useEffect(() => {
    normalMap.wrapS = THREE.RepeatWrapping
    normalMap.wrapT = THREE.RepeatWrapping
    normalMap.repeat.x = 0
    normalMap.repeat.y = 1
  }, [normalMap])

  const plankHeightComplete = plankHeight
  const windowShape = new THREE.Shape()
  windowShape
    .moveTo(-spaceBetween / 2 + sidwallTyp.log.width / 2, 0)
    .lineTo(
      -spaceBetween / 2 + sidwallTyp.log.width / 2,
      isQubus
        ? heightComplete
          ? userHeight - sidwallTyp.log.width / 2
          : plankHeight
        : plankHeightComplete +
            (sideWall.hasWedge ||
            !sidewallLeftRight ||
            plankHeightTyp === 'default' ||
            isQubus
              ? sidewallLeftRight && sideWall.hasWedge
                ? sidwallTyp.log.width / 2
                : 0
              : plankHeigtInput < spaceY
              ? 0
              : height)
    )
    .lineTo(
      -spaceBetween / 2 + sidwallTyp.log.width / 2 + attachedSize.z,
      isQubus
        ? heightComplete
          ? userHeight - sidwallTyp.log.width / 2
          : plankHeight
        : plankHeightComplete +
            (sideWall.hasWedge ||
            !sidewallLeftRight ||
            plankHeightTyp === 'default' ||
            isQubus
              ? sidewallLeftRight && sideWall.hasWedge
                ? sidwallTyp.log.width / 2
                : 0
              : plankHeigtInput < spaceY
              ? 0
              : height)
    )
    .lineTo(
      spaceBetween / 2 - sidwallTyp.log.width / 2,
      isQubus
        ? heightComplete
          ? userHeight - sidwallTyp.log.width / 2
          : plankHeight
        : plankHeightComplete +
            (isQubus || sidewallLeftRight ? sidwallTyp.log.width / 2 : 0)
    )
    .lineTo(spaceBetween / 2 - sidwallTyp.log.width / 2, 0)

  const extrudeSettings = {
    depth: sidwallTyp.glas,
    bevelEnabled: false,
    bevelSegments: 20,
    steps: 2,
    bevelSize: 1,
    bevelThickness: 1,
  }

  const material = {
    name: AR_REPLACEMENT_TARGET_MATERIAL_NAME,
    color: color,
    normalMap: normalMap,
    roughnessMap: roughnessMap,
  }

  const topSizeHelper = Helper.calculateTriangle(
    sidwallTyp.log.width,
    90,
    userAngle
  )

  const sizeHelperLength = Helper.calculateTriangle(spaceBetween, 90, userAngle)

  return (
    <group position={[0, sidwallTyp.log.width / 2, 0]}>
      <mesh
        castShadow={true}
        position={[0, 0, 0]}
        rotation={[
          Helper.convertAngleToRadiant(0),
          Helper.convertAngleToRadiant(-90),
          Helper.convertAngleToRadiant(0),
        ]}
      >
        <extrudeGeometry
          attach="geometry"
          args={[windowShape, { ...extrudeSettings }]}
        />

        <CachedMeshPhysicalMaterial {...material} />
      </mesh>

      {[...Array(2)].map((_, index) => {
        return (
          <mesh
            key={`meshPlankSideTie${index}`}
            position={[
              0,
              isQubus
                ? userHeight / 2
                : plankHeight / 2 +
                  (!sideWall.hasWedge && sidewallLeftRight && heightComplete
                    ? index
                      ? height / 2 + sidwallTyp.log.width / 2
                      : 0 + sidwallTyp.log.width / 2
                    : 0),
              index
                ? -spaceBetween / 2 + sidwallTyp.log.width / 2
                : spaceBetween / 2 - sidwallTyp.log.width / 2,
            ]}
            castShadow
            rotation={[
              Helper.convertAngleToRadiant(0),
              Helper.convertAngleToRadiant(90),
              Helper.convertAngleToRadiant(90),
            ]}
          >
            <boxGeometry
              args={[
                isQubus
                  ? userHeight - sidwallTyp.log.width / 2
                  : plankHeight +
                    (!sideWall.hasWedge && sidewallLeftRight && heightComplete
                      ? index
                        ? height - sidwallTyp.log.width / 2
                        : -sidwallTyp.log.width / 2
                      : 0),

                sidwallTyp.log.width,
                sidwallTyp.log.depth,
              ]}
            />
            <CachedMeshPhysicalMaterial color={color} />
          </mesh>
        )
      })}

      {[...Array(heightComplete ? 2 : 1)].map((_, index) => {
        return (
          <mesh
            key={`meshPlankTopTie` + index}
            position={[
              !sidewallLeftRight
                ? logBarSize.y / 2 - sidwallTyp.log.depth / 2
                : type === Keys.Seitenwände.Values.SeitenwandRechts.key
                ? heightComplete
                  ? 0
                  : -(logBarSize.y / 2 - sidwallTyp.log.width / 2)
                : heightComplete
                ? 0
                : logBarSize.y / 2 - sidwallTyp.log.width / 2,

              (isQubus
                ? heightComplete
                  ? userHeight - sidwallTyp.log.width / 2
                  : plankHeight
                : plankHeightComplete) +
                (!sideWall.hasWedge && sidewallLeftRight && heightComplete
                  ? index
                    ? height / 2
                    : isQubus
                    ? -userHeight + sidwallTyp.log.width
                    : height
                  : 0),

              isQubus
                ? index
                  ? 0
                  : heightComplete
                  ? 0
                  : 0
                : index
                ? attachedSize.z / 2 - sidwallTyp.log.width / 2
                : heightComplete && sidewallLeftRight
                ? -spaceBetween / 2 +
                  attachedSize.z / 2 +
                  sidwallTyp.log.width / 2
                : 0,
            ]}
            castShadow
            rotation={[
              Helper.convertAngleToRadiant(
                90 +
                  (!sideWall.hasWedge &&
                  sidewallLeftRight &&
                  heightComplete &&
                  !isQubus
                    ? index
                      ? userAngle
                      : 0
                    : 0)
              ),
              Helper.convertAngleToRadiant(0),
              Helper.convertAngleToRadiant(90),
            ]}
          >
            <boxGeometry
              args={[
                (!sideWall.hasWedge || heightComplete) &&
                sidewallLeftRight &&
                !isQubus
                  ? index
                    ? sizeHelperLength.b + topSizeHelper.b / 2 - attachedSize.z
                    : heightComplete
                    ? attachedSize.z
                    : spaceBetween
                  : sizeHelperLength.b,
                logBarSize.y,
                logBarSize.x,
              ]}
            />
            <CachedMeshPhysicalMaterial color={color} />
          </mesh>
        )
      })}
    </group>
  )
}
